import { HttpClient } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { FeatureToggle } from '@Enums';
import { FeatureManagerService } from './global/feature-manager/feature-manager.service';
import { AppService } from '@App';
import { IConfigService } from '@Config';
import { BaseService } from '@Services';
import moment from 'moment';
import { EMPTY, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { SubSink } from 'subsink';

export interface CaseWorkbenchTaskNoteResponse {
    id: string;
    caseId: string;
    clientId: string;
    userDisplayName: string;
    userRole: string;
    message: string;
    isInternalNote: boolean;
    createdBy: string;
    createdOn: string;
    createdOnDisplay?: string;
    updatedBy?: string;
    updatedOn?: string;
    updatedOnDisplay?: string;
    taskName?: string;
    taskId?: string;
    subtitle?: string;
}

export interface CreateCaseWorkbenchTaskNoteRequest {
    id?: string;
    message: string;
    isInternalNote: boolean;
}

@Injectable()
export class UWTaskNotesDataService extends BaseService implements OnDestroy {
    private readonly featureManagerService = inject(FeatureManagerService);
    publicApiBaseUrl: string;

    // Case Task Notes State
    private caseTaskNotesLoaded = false;
    private caseIdForLoadedCaseTaskNotes = '';
    private caseTaskNotesSub = new ReplaySubject<CaseWorkbenchTaskNoteResponse[]>(1);
    public caseTaskNotes$ = this.caseTaskNotesSub.asObservable();
    private caseTaskNotesRequests = new Map<string, Observable<CaseWorkbenchTaskNoteResponse[]>>();

    private subs = new SubSink();

    constructor(
        public appService: AppService,
        public httpClient: HttpClient,
        public configService: IConfigService
    ) {
        super(appService, configService, httpClient);
        this.publicApiBaseUrl = this.configService.getConfiguration().publicApiUrl;
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    private caseTaskNotesAreLoaded = (caseId: string) =>
        caseId === this.caseIdForLoadedCaseTaskNotes && this.caseTaskNotesLoaded;

    getTaskNotes(caseId: string, taskId: string): Observable<CaseWorkbenchTaskNoteResponse[]> {
        const clientId = this.appService.getClientId('');
        return super.getData<CaseWorkbenchTaskNoteResponse[]>(
            `${this.publicApiBaseUrl}client/${clientId}/case/${caseId}/workbenchtask/${taskId}/notes`
        );
    }

    reloadCaseTaskNotesByCaseId(caseId: string, hideWaiting = false): Observable<CaseWorkbenchTaskNoteResponse[]> {
        return this.requestCaseTaskNotes(caseId, hideWaiting, false);
    }

    private requestCaseTaskNotes(caseId: string, hideWaiting: boolean, reuseInFlight: boolean): Observable<CaseWorkbenchTaskNoteResponse[]> {
        const clientId = this.appService.getClientId('');
        const url = `${this.publicApiBaseUrl}client/${clientId}/case/${caseId}/CaseTask/notes/internal`;
        const enabled$ = clientId
            ? this.featureManagerService.isEnabled(FeatureToggle.ConfigWorkbenchDeduplicateApiCalls, clientId).pipe(
                catchError(error => {
                    this.appService.showResponseErrorMsg(error);
                    return of(false);
                })
            )
            : of(false);
        const request = enabled$.pipe(
            take(1),
            switchMap(deduplicate => {
                const existingRequest = deduplicate && reuseInFlight && this.caseTaskNotesRequests.get(url);
                if (existingRequest) return existingRequest;

                const response = super.getData<CaseWorkbenchTaskNoteResponse[]>(url, null, hideWaiting).pipe(
                    map(notes => {
                        const sortedNotes = [...notes].sort((a, b) =>
                            moment(b.createdOn).diff(moment(a.createdOn), 'seconds')
                        );

                        sortedNotes.forEach(note => {
                            note.createdOnDisplay = moment.utc(note.createdOn).tz('America/Chicago').format('M/D/YYYY h:mm a');
                            if (note.updatedOn) {
                                note.updatedOnDisplay = moment.utc(note.updatedOn).tz('America/Chicago').format('M/D/YYYY h:mm a');
                            }
                        });

                        return sortedNotes;
                    })
                );
                const publish = (sortedNotes: CaseWorkbenchTaskNoteResponse[]) => {
                    this.caseTaskNotesSub.next(sortedNotes);
                    this.caseTaskNotesLoaded = true;
                    this.caseIdForLoadedCaseTaskNotes = caseId;
                };
                if (!deduplicate) return response.pipe(tap(publish));

                // A newer reload replaces the map entry; an older request must not publish over it or remove it.
                const sharedRequest = response.pipe(
                    tap(sortedNotes => {
                        if (this.caseTaskNotesRequests.get(url) === sharedRequest) publish(sortedNotes);
                    }),
                    finalize(() => {
                        if (this.caseTaskNotesRequests.get(url) === sharedRequest) this.caseTaskNotesRequests.delete(url);
                    }),
                    shareReplay({ bufferSize: 1, refCount: false })
                );
                this.caseTaskNotesRequests.set(url, sharedRequest);
                return sharedRequest;
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        // Preserve fire-and-forget reloads; BaseService already reports HTTP errors. HTTP completes on its own, so the subscription isn't retained.
        request.pipe(catchError(() => EMPTY)).subscribe();
        return this.caseTaskNotes$;
    }

    getCaseTaskNotesByCaseId(caseId: string): Observable<CaseWorkbenchTaskNoteResponse[]> {
        if (this.caseTaskNotesAreLoaded(caseId)) return this.caseTaskNotes$;

        return this.requestCaseTaskNotes(caseId, false, true);
    }

    addTaskNote(caseId: string, taskId: string, request: CreateCaseWorkbenchTaskNoteRequest): Observable<CaseWorkbenchTaskNoteResponse> {
        const clientId = this.appService.getClientId('');
        return super.postData<CaseWorkbenchTaskNoteResponse>(
            `${this.publicApiBaseUrl}client/${clientId}/case/${caseId}/workbenchtask/${taskId}/notes`,
            request
        );
    }
    
    updateTaskNote(caseId: string, taskId: string, request: CreateCaseWorkbenchTaskNoteRequest): Observable<CaseWorkbenchTaskNoteResponse[]> {
        const clientId = this.appService.getClientId('');
        return super.patchData<CaseWorkbenchTaskNoteResponse[]>(
            `${this.publicApiBaseUrl}client/${clientId}/case/${caseId}/workbenchtask/${taskId}/notes`,
            request
        );
    }
}
